/**
 * /settings/integrations (v7, admin-only) — tela de Integrações no padrão
 * TiFlux: breadcrumb "Configurações / Geral / Integrações", abas ink
 * (Comunicação | Financeiro | Dados | API do bot), seção "Integrações
 * realizadas" (tabela) e "Integrações à realizar" (cards-logo). A aba
 * Comunicação reaproveita as seções da integração WhatsApp
 * (features/whatsapp/whatsapp-sections); a API do bot (spec 022) são as chaves
 * com que o fluxo da Zeepchat fala com o ERP.
 */
import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueries, useQuery } from '@tanstack/react-query';
import { ChevronDown, Database, Landmark, MessageCircle, ShieldAlert, Wallet } from 'lucide-react';
import { useAuthUser } from '@/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge, EmptyState, Skeleton } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { WhatsappSections } from '@/features/whatsapp/whatsapp-sections';
import {
  errorMessage,
  whatsappChannelsOptions,
  whatsappIntegrationOptions,
} from '@/features/whatsapp/whatsapp-queries';
import {
  ASAAS_ENVIRONMENT_LABELS,
  asaasErrorMessage,
  asaasIntegrationOptions,
  isAsaasConfigured,
} from '@/features/billing/asaas';
import { AsaasSections } from './asaas-sections';
import {
  isTifluxConfigured,
  tifluxErrorMessage,
  tifluxIntegrationOptions,
  tifluxRelativeTime,
} from './tiflux';
import { TifluxSections } from './tiflux-sections';
import { BotApiSection } from './bot-api-section';

const TAB_TRIGGER_CLASSES =
  'h-10 flex-none rounded-none px-1 text-sm font-medium data-[state=active]:text-primary after:h-0.5 after:bg-primary group-data-[orientation=horizontal]/tabs:after:bottom-0';

export function IntegrationsPage() {
  const user = useAuthUser();
  const isAdmin = user?.role === 'admin';

  // A tela INTEIRA é admin-only (mesma regra da antiga /whatsapp): a API
  // mascara/nega para os demais papéis, então renderizar as seções só
  // mostraria formulários quebrados.
  if (!isAdmin) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-lg border bg-card text-center shadow-xs">
        <ShieldAlert className="size-8 text-muted-foreground" />
        <p className="text-sm font-medium">Acesso restrito</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          As integrações são configuradas por administradores. Fale com um administrador da sua
          conta para conectar canais e serviços.
        </p>
      </div>
    );
  }

  return <IntegrationsAdmin />;
}

function IntegrationsAdmin() {
  const [tab, setTab] = useState('comunicacao');

  return (
    <div className="space-y-4">
      <Tabs value={tab} onValueChange={setTab} className="gap-4">
        <div className="rounded-lg border bg-card shadow-xs">
          <div className="px-4 pt-3">
            <nav className="text-[13px] text-muted-foreground" aria-label="Breadcrumb">
              <Link to="/settings" className="transition-colors hover:text-foreground">
                Configurações
              </Link>
              <span className="mx-1.5">/</span>
              <span>Geral</span>
              <span className="mx-1.5">/</span>
              <span className="text-foreground">Integrações</span>
            </nav>
            <h1 className="mt-2 text-xl leading-7 font-semibold">Integrações</h1>
          </div>
          <TabsList
            variant="line"
            className="mt-1 w-full justify-start gap-6 rounded-none p-0 px-4 group-data-[orientation=horizontal]/tabs:h-11"
          >
            <TabsTrigger value="comunicacao" className={TAB_TRIGGER_CLASSES}>
              Comunicação
            </TabsTrigger>
            <TabsTrigger value="financeiro" className={TAB_TRIGGER_CLASSES}>
              Financeiro
            </TabsTrigger>
            <TabsTrigger value="dados" className={TAB_TRIGGER_CLASSES}>
              Dados
            </TabsTrigger>
            <TabsTrigger value="bot" className={TAB_TRIGGER_CLASSES}>
              API do bot
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="comunicacao">
          <CommunicationTab />
        </TabsContent>
        <TabsContent value="financeiro">
          <FinanceTab />
        </TabsContent>
        <TabsContent value="dados">
          <DataTab />
        </TabsContent>
        <TabsContent value="bot">
          <BotApiSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Aba Comunicação: WhatsApp Business (zeepchat) ───────────────────────────

function CommunicationTab() {
  const [integrationQ, channelsQ] = useQueries({
    queries: [whatsappIntegrationOptions(), whatsappChannelsOptions()],
  });
  // "Gerenciar"/card-logo expandem o MESMO conteúdo (as seções da antiga
  // /whatsapp) — abaixo da tabela quando já configurada, abaixo do card-logo
  // quando ainda não há integração.
  const [managing, setManaging] = useState(false);

  const integration = integrationQ.data;
  const configured = integration != null && integration.apiKeyMasked !== null;
  const channelCount = channelsQ.data?.length;

  return (
    <div className="space-y-4">
      <SectionCard title="Integrações realizadas">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-[13px]">
                <th className="h-9 px-4 font-semibold">Tipo</th>
                <th className="h-9 px-4 font-semibold">Status</th>
                <th className="h-9 px-4 font-semibold">API KEY / Nome</th>
                <th className="h-9 px-4 font-semibold">Canais</th>
                <th className="h-9 w-28 px-4 font-semibold">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {integrationQ.isPending ? (
                <tr>
                  <td colSpan={5} className="px-4 py-3" aria-busy>
                    <Skeleton className="h-6 w-full" />
                  </td>
                </tr>
              ) : integrationQ.isError ? (
                <tr>
                  <td colSpan={5}>
                    <EmptyState
                      className="h-32"
                      message={`Erro ao carregar as integrações: ${errorMessage(integrationQ.error)}`}
                    />
                  </td>
                </tr>
              ) : !configured ? (
                <tr>
                  <td colSpan={5}>
                    <EmptyState className="h-32" message="Não há dados" />
                  </td>
                </tr>
              ) : (
                <>
                  <tr className="border-b border-divider transition-colors last:border-0 hover:bg-accent/60">
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className="flex items-center gap-2 font-medium">
                        <MessageCircle className="size-4 shrink-0 text-primary" />
                        WhatsApp Business · zeepchat
                      </span>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className="flex flex-wrap items-center gap-1.5">
                        <Badge variant={integration.active ? 'success' : 'default'}>
                          {integration.active ? 'Ativa' : 'Inativa'}
                        </Badge>
                        <Badge variant={integration.webhookRegistered ? 'success' : 'warning'}>
                          {integration.webhookRegistered ? 'Webhook registrado' : 'Webhook pendente'}
                        </Badge>
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground whitespace-nowrap">
                      {integration.apiKeyMasked}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground tabular-nums whitespace-nowrap">
                      {channelsQ.isPending
                        ? '…'
                        : `${channelCount ?? 0} ${channelCount === 1 ? 'número' : 'números'}`}
                    </td>
                    <td className="px-4 py-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-expanded={managing}
                        onClick={() => setManaging((v) => !v)}
                      >
                        Gerenciar
                        <ChevronDown
                          className={cn('size-4 transition-transform', managing && 'rotate-180')}
                        />
                      </Button>
                    </td>
                  </tr>
                  {managing ? (
                    <tr>
                      <td colSpan={5} className="bg-background/60 px-4 py-4">
                        <WhatsappSections />
                      </td>
                    </tr>
                  ) : null}
                </>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Sem integração: card-logo para iniciar a conexão (padrão TiFlux). */}
      {!integrationQ.isPending && !integrationQ.isError && !configured ? (
        <SectionCard title="Integrações à realizar">
          <div className="p-4">
            <button
              type="button"
              aria-expanded={managing}
              onClick={() => setManaging((v) => !v)}
              className={cn(
                'flex h-28 w-44 flex-col items-center justify-center gap-2 rounded-lg border bg-card transition-colors hover:border-primary hover:bg-accent/40',
                managing && 'border-primary'
              )}
            >
              <MessageCircle className="size-10 text-primary" strokeWidth={1.5} />
              <span className="text-sm font-medium">WhatsApp Business</span>
            </button>
            {managing ? (
              <div className="mt-4">
                <WhatsappSections />
              </div>
            ) : null}
          </div>
        </SectionCard>
      ) : null}
    </div>
  );
}

// ── Aba Financeiro: Asaas (v9 Etapa 2) + Conta Azul (em breve) ──────────────

function FinanceTab() {
  const integrationQ = useQuery(asaasIntegrationOptions());
  // "Gerenciar" (tabela) e o card-logo só alternam `managing`; o AsaasSections
  // é renderizado num ÚNICO branch no fim da aba (managing && integration).
  const [managing, setManaging] = useState(false);

  const integration = integrationQ.data;
  const configured = integration != null && isAsaasConfigured(integration);

  return (
    <div className="space-y-4">
      <SectionCard title="Integrações realizadas">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-[13px]">
                <th className="h-9 px-4 font-semibold">Tipo</th>
                <th className="h-9 px-4 font-semibold">Ambiente</th>
                <th className="h-9 px-4 font-semibold">Status</th>
                <th className="h-9 px-4 font-semibold">API KEY</th>
                <th className="h-9 w-28 px-4 font-semibold">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {integrationQ.isPending ? (
                <tr>
                  <td colSpan={5} className="px-4 py-3" aria-busy>
                    <Skeleton className="h-6 w-full" />
                  </td>
                </tr>
              ) : integrationQ.isError ? (
                <tr>
                  <td colSpan={5}>
                    <EmptyState
                      className="h-32"
                      message={`Erro ao carregar as integrações: ${asaasErrorMessage(integrationQ.error)}`}
                    />
                  </td>
                </tr>
              ) : !configured ? (
                <tr>
                  <td colSpan={5}>
                    <EmptyState className="h-32" message="Não há dados" />
                  </td>
                </tr>
              ) : (
                <tr className="border-b border-divider transition-colors last:border-0 hover:bg-accent/60">
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span className="flex items-center gap-2 font-medium">
                      <Wallet className="size-4 shrink-0 text-primary" />
                      Asaas
                    </span>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    {integration.environment ? (
                      <Badge variant={integration.environment === 'production' ? 'info' : 'default'}>
                        {ASAAS_ENVIRONMENT_LABELS[integration.environment]}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <Badge variant={integration.active ? 'success' : 'default'}>
                      {integration.active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground whitespace-nowrap">
                    {integration.apiKeyMasked ?? '—'}
                  </td>
                  <td className="px-4 py-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-expanded={managing}
                      onClick={() => setManaging((v) => !v)}
                    >
                      Gerenciar
                      <ChevronDown
                        className={cn('size-4 transition-transform', managing && 'rotate-180')}
                      />
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard title="Integrações à realizar">
        <div className="p-4">
          <div className="flex flex-wrap gap-3">
            {/* Sem integração: card-logo clicável para iniciar (padrão TiFlux). */}
            {!integrationQ.isPending && !integrationQ.isError && !configured ? (
              <button
                type="button"
                aria-expanded={managing}
                onClick={() => setManaging((v) => !v)}
                className={cn(
                  'flex h-28 w-44 flex-col items-center justify-center gap-2 rounded-lg border bg-card transition-colors hover:border-primary hover:bg-accent/40',
                  managing && 'border-primary'
                )}
              >
                <Wallet className="size-10 text-primary" strokeWidth={1.5} />
                <span className="text-sm font-medium">Asaas</span>
              </button>
            ) : null}
            <ComingSoonCard icon={Landmark} name="Conta Azul" />
          </div>
        </div>
      </SectionCard>

      {/* ÚNICO ponto que renderiza as seções — tanto o "Gerenciar" da tabela
          (configurada) quanto o card-logo (primeira configuração) só ligam
          `managing`. */}
      {managing && integration ? <AsaasSections integration={integration} /> : null}
    </div>
  );
}

// ── Aba Dados: TiFlux (v12) + API pública & Webhooks (em breve) ─────────────

function DataTab() {
  const integrationQ = useQuery(tifluxIntegrationOptions());
  // Mesmo padrão da aba Financeiro: "Gerenciar" (tabela) e o card-logo só
  // alternam `managing`; o TifluxSections é renderizado num ÚNICO branch no
  // fim da aba (managing && integration).
  const [managing, setManaging] = useState(false);

  const integration = integrationQ.data;
  const configured = integration != null && isTifluxConfigured(integration);
  const lastResult = integration?.lastResult ?? null;

  return (
    <div className="space-y-4">
      <SectionCard title="Integrações realizadas">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-[13px]">
                <th className="h-9 px-4 font-semibold">Tipo</th>
                <th className="h-9 px-4 font-semibold">Status</th>
                <th className="h-9 px-4 font-semibold">Token</th>
                <th className="h-9 px-4 font-semibold">Última sincronização</th>
                <th className="h-9 w-28 px-4 font-semibold">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {integrationQ.isPending ? (
                <tr>
                  <td colSpan={5} className="px-4 py-3" aria-busy>
                    <Skeleton className="h-6 w-full" />
                  </td>
                </tr>
              ) : integrationQ.isError ? (
                <tr>
                  <td colSpan={5}>
                    <EmptyState
                      className="h-32"
                      message={`Erro ao carregar as integrações: ${tifluxErrorMessage(integrationQ.error)}`}
                    />
                  </td>
                </tr>
              ) : !configured ? (
                <tr>
                  <td colSpan={5}>
                    <EmptyState className="h-32" message="Não há dados" />
                  </td>
                </tr>
              ) : (
                <tr className="border-b border-divider transition-colors last:border-0 hover:bg-accent/60">
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span className="flex items-center gap-2 font-medium">
                      <TifluxMark className="size-5 rounded text-[9px]" />
                      TiFlux
                    </span>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <Badge variant={integration.enabled ? 'success' : 'default'}>
                      {integration.enabled ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground whitespace-nowrap">
                    {integration.tokenMasked ?? '—'}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    {integration.lastSyncAt ? (
                      <span className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">
                          {tifluxRelativeTime(integration.lastSyncAt)}
                        </span>
                        {lastResult ? (
                          <Badge variant={lastResult.ok ? 'success' : 'destructive'}>
                            {lastResult.ok ? 'OK' : 'Erro'}
                          </Badge>
                        ) : null}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-expanded={managing}
                      onClick={() => setManaging((v) => !v)}
                    >
                      Gerenciar
                      <ChevronDown
                        className={cn('size-4 transition-transform', managing && 'rotate-180')}
                      />
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard title="Integrações à realizar">
        <div className="p-4">
          <div className="flex flex-wrap gap-3">
            {/* Sem integração: card-logo clicável para iniciar (padrão TiFlux). */}
            {!integrationQ.isPending && !integrationQ.isError && !configured ? (
              <button
                type="button"
                aria-expanded={managing}
                onClick={() => setManaging((v) => !v)}
                className={cn(
                  'flex h-28 w-44 flex-col items-center justify-center gap-2 rounded-lg border bg-card transition-colors hover:border-primary hover:bg-accent/40',
                  managing && 'border-primary'
                )}
              >
                <TifluxMark className="size-10 rounded-lg text-base" />
                <span className="text-sm font-medium">TiFlux</span>
              </button>
            ) : null}
            <ComingSoonCard icon={Database} name="API pública & Webhooks" />
          </div>
        </div>
      </SectionCard>

      {/* ÚNICO ponto que renderiza as seções — tanto o "Gerenciar" da tabela
          (configurada) quanto o card-logo (primeira configuração) só ligam
          `managing`. */}
      {managing && integration ? <TifluxSections integration={integration} /> : null}
    </div>
  );
}

/** Wordmark textual do TiFlux (sem imagem externa) — quadrado com "TF". */
function TifluxMark({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex shrink-0 items-center justify-center bg-primary/10 font-bold tracking-tight text-primary',
        className
      )}
    >
      TF
    </span>
  );
}

// ── Blocos visuais ──────────────────────────────────────────────────────────

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border bg-card shadow-xs">
      <div className="border-b px-4 py-3">
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      {children}
    </section>
  );
}

/** Card-logo desabilitado (cinza + badge "Em breve"). */
function ComingSoonCard({
  icon: Icon,
  name,
}: {
  icon: typeof Database;
  name: string;
}) {
  return (
    <div
      aria-disabled
      className="relative flex h-28 w-44 flex-col items-center justify-center gap-2 rounded-lg border border-divider bg-muted/40 text-muted-foreground"
    >
      <Badge className="absolute top-2 right-2">Em breve</Badge>
      <Icon className="size-10 opacity-60" strokeWidth={1.5} />
      <span className="px-2 text-center text-sm font-medium">{name}</span>
    </div>
  );
}
